import "./_style.less";
import { useBookingCounseling } from "../../providers/booking-counseling.provider";
import { Col, Divider, message, Row, Space, Typography } from "antd";
import {
  VuiButton,
  VuiFormCard,
  VuiSpin,
} from "../../../../@vendor/components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LeftOutlined } from "@ant-design/icons";
import SessionCard from "../session-card";
import { useGetSchedule } from "../../../schedule/hooks/use-get-schedule";
import ScheduleChip from "../schedule-chip";
import ScheduleCalendar from "../schedule-calendar";
import ActionFormPatient from "../../../customer/components/actions/action-form-patient";
import CustomerCard from "../../../customer/components/customer-card";
import { useGetCustomer } from "../../../customer/hooks/use-get-customer";
import PackageRepository from "../../../../repositories/PackageRepository";
import { AxiosResponse } from "axios";
import {
  formatDate,
  handleErrorLoadDataResponse,
  sortScheduleTimeOptions,
} from "../../../../@vendor/utils";
import {
  Schedule,
  ScheduleTime,
  ScheduleTimeOptions,
} from "../../../../models/Schedule";
import { Moment } from "moment/moment";
import moment from "moment";
import clsx from "clsx";
import { useAppSelector } from "../../../../stores/hooks";
import BookingCounselingGuestPatient from "../booking-counseling-guest-patient";
import BookingCounselingMultipleSession from "../booking-counseling-multiple-session";
import { useWindowSize } from "usehooks-ts";

const BookingCounselingStepSchedule = () => {
  const { t } = useTranslation();
  const { onPrevStep, currentStep, form } = useBookingCounseling();
  const { values, setFieldValue } = form;
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const { account } = useAppSelector((state) => state.system);
  const isLogin = useMemo(() => {
    return !!account?.id;
  }, [account]);
  const [packageData, setPackageData] = useState({
    five_session_discount_percentage: 0,
  });
  const [currentActiveIndex, setCurrentActiveIndex] = useState<number>();

  const maxSession = useMemo(() => {
    if (values?.session === "five-session") return 5;
    return 1;
  }, [values?.session]);

  const {
    data: schedules,
    isFetching: isFetchingSchedule,
    loadData: loadSchedules,
  } = useGetSchedule();

  const loadPackage = useCallback(async () => {
    await PackageRepository.show()
      .then((response: AxiosResponse) => {
        const { data: responseData } = response.data;
        setPackageData(responseData);
      })
      .catch(() => {
        handleErrorLoadDataResponse(t, t("common.text.package"));
      });
  }, []);

  const {
    data: patients,
    isFetching: isFetchingPatient,
    loadData: loadPatients,
  } = useGetCustomer();

  const handleSelectDateMultiple = useCallback(
    (s: Schedule, index: number) => {
      const exists =
        Array.isArray(values.schedule_ids) &&
        values.schedule_ids.includes(Number(s.id));

      let newSelected: number[] = [];
      let timeIdSelected: number[] = values.time_ids ?? [];
      let timeSelected: any[] = values.time ?? [];
      if (exists) {
        newSelected =
          values?.schedule_ids?.filter((si: number) => si !== s.id) ?? [];
        timeIdSelected = values?.time_ids.filter(
          (_: number, idx: number) => idx !== index
        );
        timeSelected = values?.time.filter(
          (_: any, idx: number) => idx !== index
        );
      } else {
        if (values.schedule_ids?.length < maxSession) {
          newSelected = [...values.schedule_ids, s.id];
          newSelected = newSelected
            .map((id) => {
              const sch = schedules.find((sc) => sc.id === id);
              return sch ? { id, date: sch.date } : null;
            })
            .filter(
              (item): item is { id: number; date: string } => item !== null
            )
            .sort(
              (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
            )
            .map((item) => item.id);
        } else {
          message.warning(`You can only select up to ${maxSession} sessions.`);
          newSelected = values.schedule_ids ?? [];
        }
      }

      setFieldValue("schedule_ids", newSelected);
      setFieldValue(
        "schedules",
        newSelected
          .map((id: number) => {
            const sch = schedules.find((sc) => sc.id === id);
            return sch ? sch : null;
          })
          .filter((item): item is typeof schedules[0] => item !== null)
      );
      setFieldValue("time_ids", timeIdSelected);
      setFieldValue("time", timeSelected);

      if (!exists) {
        setTimeout(() => {
          setCurrentActiveIndex(values.schedule_ids?.length);
          const el = document.getElementById(
            `counseling-session-${values.schedule_ids?.length}`
          );
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 150);
      } else {
        setTimeout(() => {
          setCurrentActiveIndex(undefined);
        }, 150);
      }
    },
    [values, maxSession, schedules]
  );

  const handleSelectDate = useCallback(
    (date: Moment) => {
      const formattedDate = date.format("YYYY-MM-DD");
      if (
        values?.schedule_date &&
        values?.schedule_date?.date === formattedDate
      ) {
        setFieldValue("schedule_date", null);
        setFieldValue("schedule_times", []);
        return;
      }

      const findDate = schedules.find(
        (calendar) => calendar.date === formattedDate
      );

      if (findDate) {
        const selectedDateTimes = findDate.times as ScheduleTimeOptions[];
        setFieldValue("schedule_date", findDate);
        setFieldValue("schedule_times", selectedDateTimes);
      }

      setTimeout(() => {
        const el = document.getElementById(`counseling-session-1`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 150);
    },
    [schedules, values?.schedule_date]
  );

  useEffect(() => {
    if (values?.psychologist_id && values?.type) {
      loadSchedules({
        psychologist: values?.psychologist_id,
        type: values?.type,
        session_type: "COUNSELING",
      }).then();
    }
  }, [values?.psychologist_id, values?.type]);

  useEffect(() => {
    if (currentStep === 1 && isLogin) {
      loadPatients().then();
    }
  }, [currentStep, isLogin]);

  useEffect(() => {
    if (currentStep === 1) {
      loadPackage().then();
    }
  }, [currentStep]);

  if (currentStep !== 1) {
    return null;
  }

  return (
    <Row gutter={[20, 20]} id="booking-counseling-step-schedule">
      <Col md={7} xs={24}>
        <Space size={20} direction="vertical" style={{ width: "100%" }}>
          <VuiFormCard className="psychologist-info">
            <div className="avatar-wrapper">
              <img
                className="avatar-image"
                src={values?.psychologist?.photo?.url}
                alt={values?.psychologist?.photo?.url}
              />
            </div>
            <div className="psychologist-info-wrapper">
              <div className="psychologist-info-name">
                {values?.psychologist?.name}
              </div>
              <div
                className="psychologist-info-title"
                dangerouslySetInnerHTML={{
                  __html: values?.psychologist?.title || "",
                }}
              />

              <VuiButton
                buttonProps={{
                  type: "primary",
                  className: "vui-button primary-outline",
                  icon: <LeftOutlined />,
                  onClick: () => {
                    onPrevStep();
                  },
                  size: isMobile ? "small" : "middle",
                }}
                label={t("common.button.changePsychologist")}
              />
            </div>
          </VuiFormCard>

          <VuiFormCard className="psychologist-category">
            <div className="psychologist-category-title">
              {t("common.text.category")}
            </div>
            <div className="psychologist-category-name">
              {values?.category?.name}
            </div>
          </VuiFormCard>
        </Space>
      </Col>

      <Col md={17} xs={24}>
        <VuiFormCard>
          <div className="schedule-session-section">
            <div className="schedule-section-title">
              {t("common.text.bookYourSession")}
            </div>
            <Row gutter={[20, 20]}>
              <Col md={8} xs={24}>
                <SessionCard
                  isActive={values?.session === "single-session"}
                  onClick={() => {
                    setFieldValue("session", "single-session");
                    setFieldValue("discount", 0);
                    setFieldValue("schedule_dates", []);
                    setFieldValue("schedule_ids", []);
                    setFieldValue("time_ids", []);
                    setFieldValue("time", []);
                    setFieldValue("schedules", []);
                  }}
                  label={t("common.text.singleSession")}
                />
              </Col>
              <Col md={8} xs={24}>
                <SessionCard
                  discount={packageData?.five_session_discount_percentage}
                  isActive={values?.session === "five-session"}
                  onClick={() => {
                    setFieldValue("session", "five-session");
                    setFieldValue(
                      "discount",
                      packageData?.five_session_discount_percentage
                    );
                    setFieldValue("schedule_date", null);
                    setFieldValue("schedule_id", null);
                  }}
                  label={t("common.text.fiveSessions")}
                />
              </Col>
            </Row>
          </div>

          <div className="schedule-method-section">
            <div className="schedule-section-title">
              {t("common.text.counselingAndPsychotherapyMethod")}
            </div>
            <Row gutter={[20, 20]}>
              <Col md={8} xs={24}>
                <SessionCard
                  isActive={values?.type === "Online"}
                  onClick={() => setFieldValue("type", "Online")}
                  label={
                    <>
                      <span>{t("common.text.online")}</span>
                      <span style={{ marginLeft: 4, fontWeight: 400 }}>
                        {t("common.text.byGoogleMeet")}
                      </span>
                    </>
                  }
                />
              </Col>
              <Col md={8} xs={24}>
                <SessionCard
                  isActive={values?.type === "Offline"}
                  onClick={() => setFieldValue("type", "Offline")}
                  label={
                    <>
                      <span>{t("common.text.faceToFace")}</span>
                      <span style={{ marginLeft: 4, fontWeight: 400 }}>
                        {t("common.text.atOurOffice")}
                      </span>
                    </>
                  }
                />
              </Col>
            </Row>
          </div>

          <Divider />

          <VuiSpin spinning={isFetchingSchedule}>
            {schedules?.length > 0 ? (
              <>
                <div className="schedule-date-section">
                  <div className="schedule-section-title">
                    {t("common.text.selectDateAndTime")}
                  </div>

                  <div className="schedule-section-subtitle">
                    {t("common.text.date")}
                  </div>
                  <Row gutter={[16, 16]}>
                    <Col md={16} xs={24}>
                      <Row gutter={[16, 16]}>
                        {schedules.slice(0, 6).map((schedule, index) => (
                          <Col md={8} xs={12} key={`schedule_chip_${index}`}>
                            <ScheduleChip
                              onClick={(s) => {
                                if (values?.session !== "single-session") {
                                  handleSelectDateMultiple(s, index);
                                } else {
                                  handleSelectDate(moment(s?.date));
                                }
                              }}
                              value={schedule}
                              isSelected={
                                values?.session !== "single-session"
                                  ? values?.schedule_ids?.includes(schedule?.id)
                                  : values?.schedule_date?.date ===
                                    moment(schedule?.date).format("YYYY-MM-DD")
                              }
                            />
                          </Col>
                        ))}
                      </Row>
                    </Col>
                    <Col md={8} xs={24}>
                      <ScheduleCalendar
                        data={schedules}
                        selectedData={
                          values?.session === "single-session"
                            ? values?.schedule_date
                              ? [values?.schedule_date]
                              : []
                            : values?.schedules
                        }
                        onSelectDate={(value) => {
                          if (values?.session === "single-session") {
                            if (value?.length) {
                              setFieldValue("schedule_date", value[0]);
                            } else {
                              setFieldValue("schedule_date", null);
                            }
                          } else {
                            setFieldValue(
                              "schedule_ids",
                              value?.map((v) => v?.id) ?? []
                            );
                            setFieldValue("schedules", value);
                          }
                        }}
                        isMultiple={values?.session !== "single-session"}
                      />
                    </Col>
                  </Row>
                </div>

                {values?.session === "five-session" ? (
                  <BookingCounselingMultipleSession
                    activeIndex={currentActiveIndex}
                    schedules={schedules}
                  />
                ) : (
                  <>
                    {values?.schedule_date && (
                      <Row
                        gutter={[16, 16]}
                        style={{ marginTop: 40 }}
                        id={`counseling-session-1`}
                      >
                        <Col md={11} xs={24}>
                          <div className="schedule-section-subtitle">
                            {t("common.text.duration")}
                          </div>
                          <Space
                            size={16}
                            direction="vertical"
                            style={{ width: "100%" }}
                          >
                            <div
                              className={clsx("primary-btn-type", {
                                selected: values?.schedule_duration === 60,
                                disabled:
                                  (
                                    values?.schedule_times as ScheduleTime[]
                                  ).filter(
                                    (time) =>
                                      time?.is_available_in_sixty_minutes
                                  ).length === 0,
                              })}
                              onClick={() => {
                                if (
                                  (
                                    values?.schedule_times as ScheduleTime[]
                                  ).filter(
                                    (time) =>
                                      time?.is_available_in_sixty_minutes
                                  ).length !== 0
                                ) {
                                  setFieldValue("schedule_duration", 60);
                                  setFieldValue("schedule", null);
                                  setFieldValue("schedule_id", null);
                                }
                              }}
                            >
                              {t("common.text.minutesItem", { item: 60 })}
                            </div>
                            <div
                              className={clsx("primary-btn-type", {
                                selected: values?.schedule_duration === 90,
                                disabled:
                                  (
                                    values?.schedule_times as ScheduleTime[]
                                  ).filter(
                                    (time) =>
                                      time?.is_available_in_ninety_minutes
                                  ).length === 0,
                              })}
                              onClick={() => {
                                if (
                                  (
                                    values?.schedule_times as ScheduleTime[]
                                  ).filter(
                                    (time) =>
                                      time?.is_available_in_ninety_minutes
                                  ).length !== 0
                                ) {
                                  setFieldValue("schedule_duration", 90);
                                  setFieldValue("schedule", null);
                                  setFieldValue("schedule_id", null);
                                }
                              }}
                            >
                              {t("common.text.minutesItem", { item: 90 })}
                            </div>
                          </Space>
                        </Col>
                        <Col md={2} xs={2} style={{ textAlign: "center" }}>
                          <Divider
                            type="vertical"
                            style={{ height: "100%", borderColor: "#E0E2E3" }}
                          />
                        </Col>
                        {values?.schedule_duration ? (
                          <Col md={11} xs={24}>
                            <div className="schedule-section-subtitle">
                              {t("common.text.time")}
                            </div>
                            <Row gutter={[16, 16]}>
                              {sortScheduleTimeOptions(
                                values?.schedule_times ?? []
                              )?.map((st, index) => {
                                const isDisabled =
                                  values?.schedule_duration === 60
                                    ? !st.is_available_in_sixty_minutes
                                    : !st.is_available_in_ninety_minutes;

                                return (
                                  <Col
                                    md={8}
                                    xs={12}
                                    key={`schedule_time_${index}`}
                                  >
                                    <div
                                      className={clsx("primary-btn-type", {
                                        selected:
                                          values?.schedule_id === st?.id,
                                        disabled: isDisabled,
                                      })}
                                      onClick={() => {
                                        if (!isDisabled) {
                                          setFieldValue("schedule_id", st?.id);
                                          setFieldValue("schedule", st);
                                        }
                                      }}
                                    >
                                      {formatDate(st.timestamp, "HH:mm")}
                                    </div>
                                  </Col>
                                );
                              })}
                            </Row>
                          </Col>
                        ) : null}
                      </Row>
                    )}
                  </>
                )}
              </>
            ) : (
              <Row gutter={[20, 20]}>
                <Col xs={24}>
                  <Typography.Text style={{ color: "red" }}>
                    {t("common.text.noScheduleAvailable")}
                  </Typography.Text>
                </Col>
              </Row>
            )}
          </VuiSpin>

          <Divider />

          {isLogin ? (
            <VuiSpin spinning={isFetchingPatient}>
              <div className="patient-section-wrapper">
                <div className="patient-section-header">
                  <div className="schedule-section-title">
                    {t("common.text.choosePatient")}
                  </div>

                  <ActionFormPatient refetch={loadPatients} />
                </div>

                <Row gutter={[16, 16]}>
                  {patients.map((p, index) => (
                    <Col md={12} xs={24} key={`patients_${index}`}>
                      <CustomerCard
                        data={p}
                        onSelect={(data) => {
                          if (values?.customer_id === data?.id) {
                            setFieldValue("customer", null);
                            setFieldValue("customer_id", null);
                          } else {
                            setFieldValue("customer", data);
                            setFieldValue("customer_id", data?.id);
                          }
                        }}
                        refetch={loadPatients}
                        isSelected={values?.customer_id === p?.id}
                      />
                    </Col>
                  ))}
                </Row>
              </div>
            </VuiSpin>
          ) : (
            <BookingCounselingGuestPatient />
          )}
        </VuiFormCard>
      </Col>
    </Row>
  );
};

export default BookingCounselingStepSchedule;
